import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import type {
  Collection,
  ApiPage,
  ApiResource,
  Paginated,
} from "@/lib/api/types"
import type {
  AddressGap,
  AddressHistory,
  Coverage,
  CreateIndividualInput,
  EmploymentHistory,
  HistoryEntry,
  DBSCheck,
  IdentityDocuments,
  Individual,
  IndividualAddress,
  IndividualEmployment,
  ApiAddressGap,
  ApiCoverage,
  ApiDBSCheck,
  IndividualFilters,
  ApiIndividual,
  ApiIndividualAddress,
  ApiIdentityDocument,
  ApiIndividualEmployment,
  UpdateIndividualInput,
} from "@/types/individual"
import { toIdentityDocument } from "@/queries/document-readings"
import { toEvidence } from "@/queries/evidence"
import type { Onboarding } from "@/types/onboarding"
import type { Requirements } from "@/types/requirement"

const listPageSize = 100
const exportPageSize = 100

export type IndividualListFilters = IndividualFilters

export function toIndividual(data: ApiIndividual): Individual {
  return {
    id: data.id,
    irn: data.irn,
    title: data.title,
    firstName: data.first_name,
    middleNames: data.middle_names,
    lastName: data.last_name,
    fullName: data.full_name,
    previousNames: data.previous_names,
    email: data.email,
    phone: data.phone,
    dateOfBirth: data.date_of_birth,
    placeOfBirth: data.place_of_birth,
    birthCountry: data.birth_country,
    nationality: data.nationality,
    secondaryNationality: data.secondary_nationality,
    residencyStatus: data.residency_status,
    gender: data.gender,
    hasNationalInsuranceNumber: data.has_national_insurance_number,
    nationalInsuranceNumberHint: data.national_insurance_number_hint ?? null,
    status: data.status,
    changes: data.changes ?? [],
    sections: data.sections ?? [],
    onboarding: data.onboarding ?? false,
    missing: data.missing ?? [],
    reviewAction: data.review_action ?? null,
    can: {
      sendRequest: data.can?.send_request ?? false,
      chase: data.can?.chase ?? false,
    },
    complianceScore: data.compliance_score,
    assessedAt: data.assessed_at,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  }
}

export function toIndividualAddress(
  data: ApiIndividualAddress
): IndividualAddress {
  return {
    id: data.id,
    line1: data.line1,
    line2: data.line2,
    city: data.city,
    county: data.county,
    postcode: data.postcode,
    country: data.country,
    residentFrom: data.resident_from,
    residentTo: data.resident_to,
    current: data.current,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  }
}

export function toAddressGap(data: ApiAddressGap): AddressGap {
  return {
    id: data.id,
    coversFrom: data.covers_from,
    coversTo: data.covers_to,
    reason: data.reason,
    explanation: data.explanation,
    recordedById: data.recorded_by_id,
    createdAt: data.created_at,
  }
}

export function toIndividualEmployment(
  data: ApiIndividualEmployment
): IndividualEmployment {
  return {
    id: data.id,
    kind: data.kind,
    isWork: data.is_work,
    employer: data.employer,
    position: data.position,
    natureOfBusiness: data.nature_of_business,
    city: data.city,
    country: data.country,
    regulated: data.regulated,
    employerFrn: data.employer_frn,
    startedOn: data.started_on,
    endedOn: data.ended_on,
    current: data.current,
    reasonForLeaving: data.reason_for_leaving,
    explanation: data.explanation,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  }
}

export function toDBSCheck(data: ApiDBSCheck): DBSCheck {
  return {
    id: data.id,
    level: data.level,
    certificateNumber: data.certificate_number,
    outcome: data.outcome,
    issuedOn: data.issued_on,
    nextDueOn: data.next_due_on,
    current: data.current,
    title: data.title,
    superseded: data.superseded,
    standing: data.standing,
    due: data.due,
    hasDisclosure: data.has_disclosure,
    updateService: data.update_service,
    recordedById: data.recorded_by_id,
    certificate: data.certificate ? toEvidence(data.certificate) : null,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  }
}

export function toCoverage(data: ApiCoverage): Coverage {
  return {
    windowFrom: data.window_from,
    windowTo: data.window_to,
    covered: data.covered,
    gaps: data.gaps,
  }
}

export function toIndividualPayload(
  input: UpdateIndividualInput | Record<string, unknown>
): Record<string, unknown> {
  const payload: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(input)) {
    if (value !== undefined) {
      payload[key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)] =
        value
    }
  }

  return payload
}

async function fetchIndividualPage(
  filters: IndividualFilters,
  page: number,
  perPage: number,
  signal?: AbortSignal
): Promise<Paginated<Individual>> {
  const sorts = {
    name: "name",
    complianceScore: "compliance_score",
    createdAt: "created_at",
  } as const
  const response = await apiFetch<ApiPage<ApiIndividual>>("/individuals", {
    params: {
      search: filters.q,
      status: filters.status,
      firm_id: filters.firmId,
      sort: filters.sort ? sorts[filters.sort] : undefined,
      order: filters.order,
      page,
      per_page: perPage,
    },
    signal,
  })

  return {
    items: response.data.map(toIndividual),
    total: response.meta.total,
    currentPage: response.meta.current_page,
    lastPage: response.meta.last_page,
  }
}

export const individualQueries = {
  list: (filters: IndividualFilters = {}) =>
    queryOptions({
      queryKey: apiKey("/individuals", filters),
      queryFn: async ({ signal }) =>
        await fetchIndividualPage(filters, 1, listPageSize, signal),
    }),

  infiniteList: (filters: IndividualListFilters = {}) =>
    infiniteQueryOptions({
      queryKey: [...apiKey("/individuals", filters), "infinite"],
      queryFn: async ({ pageParam, signal }) =>
        await fetchIndividualPage(filters, pageParam, listPageSize, signal),
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.currentPage < lastPage.lastPage
          ? lastPage.currentPage + 1
          : undefined,
    }),

  detail: (id: string) =>
    queryOptions({
      queryKey: apiKey(`/individuals/${id}`),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiIndividual>>(
          `/individuals/${id}`,
          { signal }
        )

        return toIndividual(data)
      },
    }),

  requirements: (id: string) =>
    queryOptions({
      queryKey: apiKey(`/individuals/${id}/requirements`),
      queryFn: async ({ signal }) =>
        await apiFetch<Requirements>(`/individuals/${id}/requirements`, {
          signal,
        }),
    }),

  onboarding: (id: string) =>
    queryOptions({
      queryKey: apiKey(`/individuals/${id}/onboarding`),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<Onboarding[]>>(
          `/individuals/${id}/onboarding`,
          { signal }
        )

        return data
      },
    }),

  addresses: (id: string) =>
    queryOptions({
      queryKey: apiKey(`/individuals/${id}/addresses`),
      queryFn: async ({ signal }): Promise<AddressHistory> => {
        const response = await apiFetch<{
          data: ApiIndividualAddress[]
          meta: { coverage: ApiCoverage; timeline: HistoryEntry[] }
        }>(`/individuals/${id}/addresses`, { signal })

        return {
          items: response.data.map(toIndividualAddress),
          coverage: toCoverage(response.meta.coverage),
          timeline: response.meta.timeline,
        }
      },
    }),

  addressGaps: (id: string) =>
    queryOptions({
      queryKey: apiKey(`/individuals/${id}/address-gaps`),
      queryFn: async ({ signal }): Promise<Collection<AddressGap>> => {
        const { data } = await apiFetch<{ data: ApiAddressGap[] }>(
          `/individuals/${id}/address-gaps`,
          { signal }
        )

        return { items: data.map(toAddressGap) }
      },
    }),

  employments: (id: string) =>
    queryOptions({
      queryKey: apiKey(`/individuals/${id}/employments`),
      queryFn: async ({ signal }): Promise<EmploymentHistory> => {
        const response = await apiFetch<{
          data: ApiIndividualEmployment[]
          meta: { coverage: ApiCoverage; timeline: HistoryEntry[] }
        }>(`/individuals/${id}/employments`, { signal })

        return {
          items: response.data.map(toIndividualEmployment),
          coverage: toCoverage(response.meta.coverage),
          timeline: response.meta.timeline,
        }
      },
    }),

  identityDocuments: (id: string) =>
    queryOptions({
      queryKey: apiKey(`/individuals/${id}/identity-documents`),
      queryFn: async ({ signal }): Promise<IdentityDocuments> => {
        const { data, meta } = await apiFetch<{
          data: ApiIdentityDocument[]
          meta: Omit<IdentityDocuments, "items">
        }>(`/individuals/${id}/identity-documents`, { signal })

        return { items: data.map(toIdentityDocument), ...meta }
      },
    }),

  dbsChecks: (id: string) =>
    queryOptions({
      queryKey: apiKey(`/individuals/${id}/dbs-checks`),
      queryFn: async ({ signal }): Promise<Collection<DBSCheck>> => {
        const { data } = await apiFetch<{ data: ApiDBSCheck[] }>(
          `/individuals/${id}/dbs-checks`,
          { signal }
        )

        return { items: data.map(toDBSCheck) }
      },
    }),
}

export async function loadAllIndividuals(
  filters: IndividualListFilters = {},
  signal?: AbortSignal
): Promise<Individual[]> {
  const people: Individual[] = []

  for (let page = 1; ; page += 1) {
    const result = await fetchIndividualPage(
      filters,
      page,
      exportPageSize,
      signal
    )
    people.push(...result.items)

    if (page >= result.lastPage) {
      return people
    }
  }
}

export function useCreateIndividual() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateIndividualInput) => {
      const { data } = await apiFetch<ApiResource<ApiIndividual>>(
        "/individuals",
        { method: "POST", body: toIndividualPayload(input) }
      )

      return toIndividual(data)
    },
    onSuccess: (person) => {
      queryClient.setQueryData(apiKey(`/individuals/${person.id}`), person)
    },
  })
}

export function useUpdateIndividual(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      input: UpdateIndividualInput | Record<string, unknown>
    ) => {
      const { data } = await apiFetch<ApiResource<ApiIndividual>>(
        `/individuals/${id}`,
        { method: "PATCH", body: toIndividualPayload(input) }
      )

      return toIndividual(data)
    },
    onSuccess: (person) => {
      queryClient.setQueryData(apiKey(`/individuals/${id}`), person)
    },
  })
}

export function useRevealNationalInsuranceNumber(id: string) {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiFetch<{
        data: { national_insurance_number: string }
      }>(`/individuals/${id}/national-insurance-number`)

      return data.national_insurance_number
    },
  })
}

export function useSetNationalInsuranceNumber(id: string) {
  return useMutation({
    mutationFn: async (nationalInsuranceNumber: string | null) =>
      await apiFetch<ApiResource<ApiIndividual>>(
        `/individuals/${id}/national-insurance-number`,
        {
          method: "PUT",
          body: { national_insurance_number: nationalInsuranceNumber },
        }
      ),
  })
}

export function useArchiveIndividual(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { reason: string }) => {
      const { data } = await apiFetch<ApiResource<ApiIndividual>>(
        `/individuals/${id}/archive`,
        { method: "POST", body: { reason: input.reason.trim() || null } }
      )

      return toIndividual(data)
    },
    onSuccess: (person) => {
      queryClient.setQueryData(apiKey(`/individuals/${id}`), person)
    },
  })
}

export function useForceArchiveIndividual(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { on: string; reason: string }) => {
      const { data } = await apiFetch<ApiResource<ApiIndividual>>(
        `/individuals/${id}/force-archive`,
        { method: "POST", body: input }
      )

      return toIndividual(data)
    },
    onSuccess: (person) => {
      queryClient.setQueryData(apiKey(`/individuals/${id}`), person)
    },
  })
}
